// src/features/records/components/GalleryView/components/MediaLightbox/hooks/useSignedUrls.js
import { useState, useEffect, useCallback, useRef } from 'react';
import signedUrlService from '../../../../../services/signedUrlService';

function createSignedUrlError(cause) {
  const normalizedError = {
    type: 'SIGNED_URL_FETCH_FAILED',
    code: 'gallery/signed-url-fetch-failed'
  };

  if (cause?.message) {
    normalizedError.message = cause.message;
  }

  if (cause) {
    normalizedError.cause = cause;
  }

  return normalizedError;
}

/**
 * Hook for managing signed URLs with prefetching
 * Loads current media URL and prefetches neighbors
 * 
 * @param {Array} items - Media items array
 * @param {number} activeIndex - Currently active item index
 * @param {boolean} isOpen - Whether lightbox is open
 * @param {number} prefetchCount - Number of neighbors to prefetch (default 2)
 * @returns {Object} URL state and refresh function
 */
export function useSignedUrls(items, activeIndex, isOpen, prefetchCount = 2) {
  const [urls, setUrls] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mountedRef = useRef(false);
  const inflightRef = useRef(0);
  const inflightPathsRef = useRef(new Set());
  const reqSeqRef = useRef(0); // Track request sequence to ignore stale results
  
  // Track mount status
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  
  // Safe setState helpers
  const setLoadingSafe = useCallback((v) => {
    if (mountedRef.current) setLoading(v);
  }, []);
  
  const setErrorSafe = useCallback((e) => {
    if (mountedRef.current) setError(e);
  }, []);
  
  // Reset memory when lightbox closes
  useEffect(() => {
    if (!isOpen) {
      setUrls(new Map());
      setError(null);
      setLoading(false);
      inflightPathsRef.current.clear();
    }
  }, [isOpen]);
  
  // Reset memory when items array changes (new dataset)
  useEffect(() => {
    setUrls(new Map());
    setError(null);
    inflightPathsRef.current.clear();
  }, [items]);
  
  /**
   * Get storage paths for an item based on type
   */
  const getItemPaths = useCallback((item, { includeOriginal = true } = {}) => {
    if (!item) return [];
    
    const paths = [];
    
    // For images, prefer original in the lightbox and keep thumbnails as fallback.
    if (item.type === 'image') {
      if (includeOriginal && item.storagePath) {
        paths.push(item.storagePath);
      }
      if (item.thumbnails?.['800']) {
        paths.push(item.thumbnails['800']);
      }
      if (item.thumbnails?.['400']) {
        paths.push(item.thumbnails['400']);
      }
    }
    
    // For videos, get both video and poster
    if (item.type === 'video') {
      if (item.storagePath) {
        paths.push(item.storagePath);
      }
      if (item.thumbnails?.poster) {
        paths.push(item.thumbnails.poster);
      }
    }
    
    return paths;
  }, []);
  
  /**
   * Fetch URLs for specified paths
   */
  const fetchUrls = useCallback(async (pathsToFetch) => {
    if (!Array.isArray(pathsToFetch) || pathsToFetch.length === 0) return;

    const uniquePaths = [...new Set(pathsToFetch)];
    if (uniquePaths.length === 0) return;
    
    const inflightPaths = inflightPathsRef.current;
    const pendingPaths = uniquePaths.filter((path) => !inflightPaths.has(path));
    if (pendingPaths.length === 0) return;
    
    pendingPaths.forEach((path) => inflightPaths.add(path));
    
    const seq = ++reqSeqRef.current;
    inflightRef.current += 1;
    setLoadingSafe(true);
    setErrorSafe(null);
    
    try {
      // Batch fetch signed URLs
      const results = await signedUrlService.getSignedUrls(pendingPaths);
      
      // Ignore if unmounted
      if (!mountedRef.current) return;
      
      // Merge safely into latest state (prevents lost updates)
      setUrls((prev) => {
        let changed = false;
        const merged = new Map(prev);
        for (const result of results) {
          if (result?.url) {
            const existing = merged.get(result.path);
            if (existing !== result.url) {
              merged.set(result.path, result.url);
              changed = true;
            }
          }
        }
        return changed ? merged : prev;
      });
    } catch (err) {
      console.error('Failed to fetch signed URLs:', err);
      // Only surface the error if this is still the latest request sequence
      if (mountedRef.current && seq === reqSeqRef.current) {
        setErrorSafe(createSignedUrlError(err));
      }
    } finally {
      pendingPaths.forEach((path) => inflightPaths.delete(path));
      inflightRef.current -= 1;
      setLoadingSafe(inflightRef.current > 0);
    }
  }, [setLoadingSafe, setErrorSafe]);
  
  /**
   * Load current and prefetch neighbors
   */
  useEffect(() => {
    if (!isOpen || items.length === 0) return;
    
    // Determine indices to load
    const indicesToLoad = [activeIndex];
    
    // Add neighbors for prefetching
    for (let i = 1; i <= prefetchCount; i++) {
      if (activeIndex - i >= 0) {
        indicesToLoad.push(activeIndex - i);
      }
      if (activeIndex + i < items.length) {
        indicesToLoad.push(activeIndex + i);
      }
    }
    
    const pathsToFetch = [];
    const seen = new Set();
    const inflightPaths = inflightPathsRef.current;

    for (const index of indicesToLoad) {
      const item = items[index];
      if (!item) continue;

      const paths = getItemPaths(item, { includeOriginal: index === activeIndex });
      for (const path of paths) {
        if (!path || urls.has(path) || inflightPaths.has(path) || seen.has(path)) continue;
        seen.add(path);
        pathsToFetch.push(path);
      }
    }

    if (pathsToFetch.length > 0) {
      fetchUrls(pathsToFetch);
    }
  }, [activeIndex, isOpen, items, prefetchCount, fetchUrls, getItemPaths, urls]);
  
  /**
   * Get URLs for current item
   */
  const getCurrentUrls = useCallback(() => {
    const item = items[activeIndex];
    if (!item) return { mediaUrl: null, previewUrl: null, posterUrl: null };
    
    let mediaUrl = null;
    let previewUrl = null;
    let posterUrl = null;
    
    if (item.type === 'image') {
      const fullUrl = urls.get(item.storagePath) || null;
      const thumbUrl = urls.get(item.thumbnails?.['800']) ||
        urls.get(item.thumbnails?.['400']) ||
        item.thumbnailUrl ||
        null;
      
      // Full image for final render; thumbnails stay available as a placeholder.
      mediaUrl = fullUrl || thumbUrl;
      previewUrl = thumbUrl;
    } else if (item.type === 'video') {
      mediaUrl = urls.get(item.storagePath) || item.thumbnailUrl;
      posterUrl = urls.get(item.thumbnails?.poster) || item.posterUrl;
    }
    
    return { mediaUrl, previewUrl, posterUrl };
  }, [items, activeIndex, urls]);
  
  /**
   * Refresh URL on 403 error
   */
  const refreshUrl = useCallback(async (path) => {
    try {
      const newUrl = await signedUrlService.refreshUrl(path);
      // Check if still mounted before updating state
      if (!mountedRef.current) return newUrl;
      setUrls((prev) => {
        const updated = new Map(prev);
        updated.set(path, newUrl);
        return updated;
      });
      return newUrl;
    } catch (err) {
      console.error('Failed to refresh URL:', err);
      // Don't throw if unmounted
      if (!mountedRef.current) return null;
      throw err;
    }
  }, []);
  
  return {
    ...getCurrentUrls(),
    urls, // Expose the URLs map for prefetching
    loading,
    error,
    refreshUrl
  };
}

export default useSignedUrls;
